import { startDragPreview } from "@shared/components/drag-preview/startDragPreview"
import { cx } from "@shared/components/untitledui/util/cx"
import { motion } from "motion/react"
import { useRef, type ReactNode } from "react"
import {
  DropIndicator,
  GridList,
  GridListItem,
  useDragAndDrop,
  type GridListItemProps,
  type GridListProps,
} from "react-aria-components"
import { flushSync } from "react-dom"
import styles from "./ReorderableList.module.css"

interface Props<T extends { id: string }> extends Omit<
  GridListProps<T>,
  "items" | "children" | "dragAndDropHooks"
> {
  items: T[]
  /** The ids in their new order. Called within the drop, so the caller's update renders there. */
  onReorder: (ids: string[]) => void
  /** Renders a `ReorderableItem`. */
  children: (item: T) => ReactNode
}

/**
 * A react-aria GridList whose rows reorder by drag and drop: a long press on touch, a mouse
 * drag, or the keyboard from an item's `<Button slot="drag">`. The dragged item is carried as
 * an opaque copy that glides into its new place on the drop, while the items it passes slide.
 */
export default function ReorderableList<T extends { id: string }>({
  items,
  onReorder,
  dependencies = [],
  children,
  ...props
}: Props<T>) {
  const ids = items.map((item) => item.id)
  const nativeDragStart = useRef<DragEvent | null>(null)

  const { dragAndDropHooks } = useDragAndDrop<T>({
    // The browser dims every native drag image, so an opaque copy of the row follows the
    // pointer instead. react-aria hides the native dragstart, so the list's capture handler
    // keeps it; a screen-reader drag arrives here with it already default-prevented.
    onDragStart: () => {
      const event = nativeDragStart.current
      nativeDragStart.current = null
      const row =
        event?.target instanceof Element && event.target.closest<HTMLElement>("[role=row]")
      if (event && !event.defaultPrevented && row)
        startDragPreview(event, row, { settleIntoSource: true })
    },
    getItems: (keys) => [...keys].map((key) => ({ "text/plain": String(key) })),
    getAllowedDropOperations: () => ["move"],
    onReorder: ({ keys, target }) => {
      // The lines on either side of an item are drop targets relative to the item itself.
      if (keys.has(target.key)) return
      const rest = ids.filter((id) => !keys.has(id))
      const at = rest.indexOf(String(target.key)) + (target.dropPosition === "after" ? 1 : 0)
      // Rendered within the drop: left to React's next task, the browser can paint the item
      // back in its old place first.
      flushSync(() =>
        onReorder([...rest.slice(0, at), ...ids.filter((id) => keys.has(id)), ...rest.slice(at)]),
      )
    },
    renderDropIndicator: (target) => (
      <DropIndicator target={target} className={styles["drop-indicator"]} />
    ),
  })

  return (
    <div onDragStartCapture={(event) => (nativeDragStart.current = event.nativeEvent)}>
      <GridList
        {...props}
        items={items}
        // react-aria reuses a row's rendered content until a dependency changes, and Motion
        // only measures a layout change on a render.
        dependencies={[...dependencies, ids.join()]}
        dragAndDropHooks={dragAndDropHooks}
      >
        {children}
      </GridList>
    </div>
  )
}

interface ItemProps extends Omit<GridListItemProps, "children" | "className"> {
  className?: string
  children: ReactNode
}

/**
 * A row of a `ReorderableList`. Its content is a Motion element, not the row itself: react-aria
 * renders its rows outside the components that declare them, where Motion can't reach. The
 * dragged item doesn't slide, as its copy settles it.
 */
export function ReorderableItem({ className, children, ...props }: ItemProps) {
  return (
    <GridListItem {...props} className={cx(styles.item, className)}>
      {({ isDragging }) => (
        <motion.div
          layout={isDragging ? false : "position"}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      )}
    </GridListItem>
  )
}
